<template>
  <div class="contact-form-container">
    <div class="form-header">
      <h2>{{ $t('contact.title') }}</h2>
      <p>{{ $t('contact.description') }}</p>
    </div>

    <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-position="top"
        class="contact-form"
        @submit.prevent="submitForm"
    >
      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contact.nameLabel')" prop="name">
            <el-input
                v-model="formData.name"
                :placeholder="$t('contact.namePlaceholder')"
                prefix-icon="el-icon-user"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contact.emailLabel')" prop="email">
            <el-input
                v-model="formData.email"
                :placeholder="$t('contact.emailPlaceholder')"
                prefix-icon="el-icon-message"
                type="email"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contact.phoneLabel')" prop="phone">
            <el-input
                v-model="formData.phone"
                :placeholder="$t('contact.phonePlaceholder')"
                prefix-icon="el-icon-phone"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12" :xs="24">
          <el-form-item :label="$t('contact.subjectLabel')" prop="subject">
            <el-select
                v-model="formData.subject"
                :placeholder="$t('contact.subjectPlaceholder')"
                style="width: 100%"
            >
              <el-option :label="$t('contact.subjectOptions.product')" value="产品咨询" />
              <el-option :label="$t('contact.subjectOptions.techSupport')" value="技术支持" />
              <el-option :label="$t('contact.subjectOptions.cooperation')" value="合作洽谈" />
              <el-option :label="$t('contact.subjectOptions.complaint')" value="投诉建议" />
              <el-option :label="$t('contact.subjectOptions.other')" value="其他" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item :label="$t('contact.messageLabel')" prop="message">
        <el-input
            v-model="formData.message"
            type="textarea"
            rows="6"
            :placeholder="$t('contact.messagePlaceholder')"
        />
      </el-form-item>

      <el-form-item class="form-footer">
        <el-button
            type="primary"
            native-type="submit"
            :loading="submitLoading"
            size="large"
            class="submit-btn"
        >
          {{ $t('contact.submitButton') }}
        </el-button>
      </el-form-item>
    </el-form>

    <el-dialog
        v-model="dialogVisible"
        :title="dialogTitle"
        width="30%"
        center
    >
      <div class="dialog-content">
        <i :class="dialogIcon" class="dialog-icon"></i>
        <p>{{ dialogMessage }}</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="dialogVisible = false">{{ $t('contact.dialogConfirm') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { contactApi } from '@/api';
import { useI18n } from 'vue-i18n';

export default {
  name: 'ContactForm',
  props: {
    successCallback: {
      type: Function,
      default: null
    }
  },
  setup(props) {
    const { t } = useI18n();

    const formRef = ref(null);
    const formData = reactive({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });

    const formRules = {
      name: [
        { required: true, message: t('validation.nameRequired'), trigger: 'blur' },
        { min: 2, max: 20, message: t('validation.nameLength'), trigger: 'blur' }
      ],
      email: [
        { required: true, message: t('validation.emailRequired'), trigger: 'blur' },
        { type: 'email', message: t('validation.emailInvalid'), trigger: 'blur' }
      ],
      phone: [
        { required: true, message: t('validation.phoneRequired'), trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: t('validation.phoneInvalid'), trigger: 'blur' }
      ],
      subject: [
        { required: true, message: t('validation.subjectRequired'), trigger: 'change' }
      ],
      message: [
        { required: true, message: t('validation.messageRequired'), trigger: 'blur' },
        { min: 10, max: 500, message: t('validation.messageLength'), trigger: 'blur' }
      ]
    };

    const submitLoading = ref(false);
    const dialogVisible = ref(false);
    const dialogTitle = ref('');
    const dialogMessage = ref('');
    const dialogIcon = ref('');

    const submitForm = () => {
      formRef.value.validate(async (valid) => {
        if (!valid) return;
        submitLoading.value = true;

        try {
          const res = await contactApi.submitContact(formData);

          if (res.data.ActionType === 'ok') {
            dialogTitle.value = t('dialog.successTitle');
            dialogMessage.value = t('dialog.successMessage');
            dialogIcon.value = 'el-icon-success success-icon';
            dialogVisible.value = true;
            formRef.value.resetFields();
            if (props.successCallback) {
              props.successCallback();
            }
          } else {
            throw new Error(res.data.message || t('dialog.failMessage'));
          }
        } catch (error) {
          console.error('提交留言失败:', error);
          dialogTitle.value = t('dialog.failTitle');
          dialogMessage.value = t('dialog.failMessage');
          dialogIcon.value = 'el-icon-error error-icon';
          dialogVisible.value = true;
        } finally {
          submitLoading.value = false;
        }
      });
    };

    return {
      formRef,
      formData,
      formRules,
      submitLoading,
      dialogVisible,
      dialogTitle,
      dialogMessage,
      dialogIcon,
      submitForm
    };
  }
};
</script>

<style lang="scss" scoped>
/* 保持原样，无变化 */
.contact-form-container {
  max-width: 800px;
  margin: 0 auto;

  .form-header {
    text-align: center;
    margin-bottom: 40px;

    h2 {
      font-size: 32px;
      font-weight: 700;
      color: var(--secondary-color);
      margin-bottom: 15px;
    }

    p {
      color: var(--text-light-color);
      font-size: 16px;
      max-width: 600px;
      margin: 0 auto;
    }
  }

  .contact-form {
    padding: 40px;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

    @media (max-width: 768px) {
      padding: 25px 20px;
    }

    :deep(.el-form-item__label) {
      font-weight: 600;
      color: var(--secondary-color);
    }

    :deep(.el-input__inner) {
      padding-left: 40px;
    }

    :deep(.el-input__prefix) {
      left: 10px;
      color: var(--primary-color);
    }

    .form-footer {
      margin-top: 20px;
      text-align: center;

      .submit-btn {
        padding: 12px 40px;
        font-size: 16px;
        transition: all 0.3s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(64, 158, 255, 0.3);
        }
      }
    }
  }

  .dialog-content {
    text-align: center;
    padding: 20px 0;

    .dialog-icon {
      font-size: 48px;
      margin-bottom: 15px;

      &.success-icon {
        color: #67c23a;
      }

      &.error-icon {
        color: #f56c6c;
      }
    }

    p {
      font-size: 16px;
      color: var(--text-color);
    }
  }

  .dialog-footer {
    text-align: center;
  }
}
</style>
